import {
  IonAlert,
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonChip,
  IonContent,
  IonDatetime,
  IonIcon,
  IonItem,
  IonLabel,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonText,
  useIonToast,
  useIonViewWillEnter,
} from "@ionic/react";
import { callOutline, locationOutline, personAddOutline, timeOutline } from "ionicons/icons";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";

import Header from "src/components/Header";
import {
  AccionFiscal,
  CasoFiscalizador,
  accionFiscal,
  getCasosFiscalizador,
} from "src/services/gestionAusencia";

// ------------------------------------------------------------
// Etiquetas y colores
// ------------------------------------------------------------

const ETIQUETA_ESTADO: Record<string, string> = {
  PENDIENTE: "Pendiente",
  NOTIFICADO: "Notificado",
  SIN_RESPUESTA: "Sin respuesta",
  NO_MARCO: "No marco",
  NO_VA_ASISTIR: "No va a asistir",
  PRESENCIA_NO_CONFIRMADA: "Presencia no confirmada",
  ASIGNACION_INCORRECTA: "Asignacion incorrecta",
  VA_A_LLEGAR: "Va a llegar",
  MARCO: "Marco en tiempo",
  VALIDADO_GPS: "Validado por GPS",
  DERIVADO_FISCALIZADOR: "Derivado a mi",
  VALIDADO_CC: "Validado por CC",
  VALIDADO_FISCALIZADOR: "Validado por fiscal",
  AUSENCIA_CONFIRMADA: "Ausencia confirmada",
  ASIGNACION_CORREGIDA: "Asignacion corregida",
  CERRADO: "Cerrado",
};

const COLOR_SEMAFORO: Record<string, string> = {
  ROJO: "danger",
  AMARILLO: "warning",
  VERDE: "success",
  GRIS: "medium",
};

const BORDE_SEMAFORO: Record<string, string> = {
  ROJO: "#eb445a",
  AMARILLO: "#ffc409",
  VERDE: "#2dd36f",
  GRIS: "#92949c",
};

// Casos en rojo: el operario no marco. El fiscal revisa si fue un problema
// de marcacion, si falta cargar el reemplazo o si realmente falto.
const ESTADOS_ROJOS = ["NO_VA_ASISTIR", "SIN_RESPUESTA", "NO_MARCO"];

// Estados que se agrupan en la tarjeta "Falto todo el dia"
const ESTADOS_FALTA_DIA = [...ESTADOS_ROJOS, "DERIVADO_FISCALIZADOR"];

// Acciones disponibles para el fiscal segun el estado del caso
const ACCIONES_POR_ESTADO: Record<string, AccionFiscal[]> = {
  DERIVADO_FISCALIZADOR: ["confirmar_presencia", "falto_asignacion", "cerrar"],
  PRESENCIA_NO_CONFIRMADA: ["confirmar_presencia", "falto_asignacion", "cerrar"],
  ASIGNACION_INCORRECTA: ["corregir_asignacion"],
  NO_VA_ASISTIR: ["confirmar_presencia", "falto_asignacion"],
  SIN_RESPUESTA: ["confirmar_presencia", "falto_asignacion"],
  NO_MARCO: ["confirmar_presencia", "falto_asignacion"],
};

const LABEL_ACCION: Record<AccionFiscal, string> = {
  confirmar_presencia: "Confirmar presencia",
  corregir_asignacion: "Asignacion corregida",
  cerrar: "Cerrar caso",
  falto_asignacion: "Falto a su asignacion",
  falto_dia: "Falto todo el dia",
};

const COLOR_ACCION: Record<AccionFiscal, string> = {
  confirmar_presencia: "success",
  corregir_asignacion: "warning",
  cerrar: "danger",
  falto_asignacion: "danger",
  falto_dia: "danger",
};

const MENSAJE_ACCION: Record<AccionFiscal, string> = {
  confirmar_presencia:
    "Se confirmara que el operario esta presente en su punto de servicio.",
  corregir_asignacion:
    "Registra que la asignacion ya fue corregida en EMSYS.",
  cerrar: "Se cerrara el caso. La observacion es obligatoria.",
  falto_asignacion:
    "Confirmas que el operario no fue a esta asignacion. La observacion es obligatoria.",
  falto_dia:
    "Confirmas que el operario falto a todas sus asignaciones del dia. El caso se envia a RRHH como \"Sancion a Definir por RRHH\". La observacion es obligatoria.",
};

const OBSERVACION_OBLIGATORIA: AccionFiscal[] = ["cerrar", "falto_asignacion", "falto_dia"];

// En un caso en rojo, "confirmar presencia" significa que el operario fue
// pero no quedo registrado (marcacion o biometrico).
const esPresenciaEnRojo = (accion: AccionFiscal, estado: string) =>
  accion === "confirmar_presencia" && ESTADOS_ROJOS.includes(estado);

const labelAccion = (accion: AccionFiscal, estado: string) =>
  esPresenciaEnRojo(accion, estado) ? "Problema de marcacion" : LABEL_ACCION[accion];

const mensajeAccion = (accion: AccionFiscal, estado: string) =>
  esPresenciaEnRojo(accion, estado)
    ? "El operario fue a trabajar: fue un problema de marcacion o del biometrico."
    : MENSAJE_ACCION[accion];

type ItemBandeja =
  | { tipo: "caso"; caso: CasoFiscalizador }
  | { tipo: "dia"; clave: string; casos: CasoFiscalizador[] };

/**
 * Junta en una sola tarjeta los casos de quien falto a todas sus asignaciones
 * del dia (marcados por EMSYS). El resto se muestra caso por caso, en el
 * mismo orden en que llegan.
 */
const agruparCasos = (casos: CasoFiscalizador[]): ItemBandeja[] => {
  const items: ItemBandeja[] = [];
  const grupos: Record<string, CasoFiscalizador[]> = {};
  casos.forEach((caso) => {
    if (caso.falta_dia_completo && ESTADOS_FALTA_DIA.includes(caso.estado)) {
      const clave = `${caso.operario_id}|${(caso.fecha_turno || "").substring(0, 10)}`;
      if (!grupos[clave]) {
        grupos[clave] = [];
        items.push({ tipo: "dia", clave, casos: grupos[clave] });
      }
      grupos[clave].push(caso);
    } else {
      items.push({ tipo: "caso", caso });
    }
  });
  return items;
};

const hoyIso = () => moment().format("YYYY-MM-DD");

// ------------------------------------------------------------
// Componente
// ------------------------------------------------------------

const MisCasosFiscal: React.FC = () => {
  const [present] = useIonToast();

  const [desde, setDesde] = useState<string>(hoyIso());
  const [hasta, setHasta] = useState<string>(hoyIso());
  const [soloPendientes, setSoloPendientes] = useState<boolean>(true);

  const [casos, setCasos] = useState<CasoFiscalizador[]>([]);
  const [cargando, setCargando] = useState<boolean>(false);
  const [accionActiva, setAccionActiva] = useState<{
    caso: CasoFiscalizador;
    accion: AccionFiscal;
  } | null>(null);

  // Evita aplicar respuestas viejas si el usuario cambia filtros rapido
  const consultaIdRef = useRef(0);

  const cargarCasos = useCallback(
    async (silencioso = false) => {
      const consultaId = ++consultaIdRef.current;
      if (!silencioso) setCargando(true);
      try {
        const data = await getCasosFiscalizador(desde, hasta, soloPendientes);
        if (consultaId === consultaIdRef.current) setCasos(data);
      } catch (error: any) {
        if (consultaId === consultaIdRef.current) {
          const msg =
            error?.response?.data?.error ||
            "No se pudieron obtener los casos. Intenta nuevamente.";
          present({ message: msg, duration: 3000, color: "danger" });
        }
      } finally {
        if (consultaId === consultaIdRef.current) setCargando(false);
      }
    },
    [desde, hasta, soloPendientes, present]
  );

  useIonViewWillEnter(() => {
    cargarCasos();
  });

  useEffect(() => {
    cargarCasos();
  }, [cargarCasos]);

  const ejecutarAccion = async (
    caso: CasoFiscalizador,
    accion: AccionFiscal,
    observacion: string
  ) => {
    if (OBSERVACION_OBLIGATORIA.includes(accion) && !observacion.trim()) {
      present({
        message: "La observacion es obligatoria para esta accion.",
        duration: 3000,
        color: "warning",
      });
      return;
    }
    try {
      const respuesta = await accionFiscal(caso.id, accion, observacion.trim());
      present({
        message:
          accion === "falto_dia"
            ? `Falta confirmada y enviada a RRHH${
                respuesta?.solicitud_rrhh_id ? ` (solicitud #${respuesta.solicitud_rrhh_id})` : ""
              }.`
            : "Accion registrada correctamente.",
        duration: 3000,
        color: "success",
      });
      cargarCasos(true);
    } catch (error: any) {
      const msg =
        error?.response?.data?.error || "No se pudo procesar la accion.";
      present({ message: msg, duration: 3500, color: "danger" });
    }
  };

  const normalizarFecha = (valor: string | null | undefined) =>
    (valor || "").substring(0, 10) || hoyIso();

  const renderCaso = (caso: CasoFiscalizador) => {
    const acciones = ACCIONES_POR_ESTADO[caso.estado] || [];
    const telefono = String(caso.telefono ?? "").trim();

    return (
      <IonCard
        key={caso.id}
        style={{ borderLeft: `5px solid ${BORDE_SEMAFORO[caso.semaforo] || "#92949c"}` }}
      >
        <IonCardContent>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <IonText color="dark">
                <h2 style={{ fontWeight: 700, margin: 0 }}>{caso.nombre_operario || "-"}</h2>
              </IonText>
              <p style={{ margin: "2px 0 0", fontSize: "0.85rem" }}>
                <IonIcon icon={locationOutline} style={{ verticalAlign: "middle" }} />{" "}
                {caso.punto_servicio || "-"}
              </p>
            </div>
            <IonChip color={COLOR_SEMAFORO[caso.semaforo] || "medium"} style={{ flexShrink: 0 }}>
              {ETIQUETA_ESTADO[caso.estado] || caso.estado}
            </IonChip>
          </div>

          <p style={{ margin: "6px 0 0", fontSize: "0.85rem" }}>
            CI {caso.cedula || "-"} · {moment(caso.fecha_turno).format("DD/MM/YYYY")} ·{" "}
            {(caso.hora_entrada || "--:--") + " - " + (caso.hora_salida || "--:--")}
          </p>

          <p style={{ margin: "4px 0 0", fontSize: "0.8rem", color: "#666" }}>
            <IonIcon icon={timeOutline} style={{ verticalAlign: "middle" }} />{" "}
            {caso.tiempo_en_estado_min != null
              ? `${caso.tiempo_en_estado_min} min en este estado`
              : "-"}
            {Boolean(caso.derivado_a_mi) && (
              <IonBadge color="tertiary" style={{ marginLeft: 8 }}>
                Derivado por CC
              </IonBadge>
            )}
            {caso.presencia_distancia_m != null && (
              <IonBadge color="medium" style={{ marginLeft: 8 }}>
                GPS a {caso.presencia_distancia_m}m
              </IonBadge>
            )}
          </p>

          {caso.observacion_cc && (
            <p style={{ margin: "4px 0 0", fontSize: "0.8rem", fontStyle: "italic" }}>
              CC: {caso.observacion_cc}
            </p>
          )}

          <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {telefono && (
              <IonButton size="small" fill="outline" href={`tel:${telefono}`}>
                <IonIcon icon={callOutline} slot="start" />
                Llamar
              </IonButton>
            )}
            {acciones.map((accion) => (
              <IonButton
                key={accion}
                size="small"
                color={COLOR_ACCION[accion]}
                onClick={() => setAccionActiva({ caso, accion })}
              >
                {labelAccion(accion, caso.estado)}
              </IonButton>
            ))}
            {ESTADOS_ROJOS.includes(caso.estado) && botonReemplazo}
          </div>
        </IonCardContent>
      </IonCard>
    );
  };

  // Si el reemplazo se carga, EMSYS cierra el caso solo.
  const botonReemplazo = (
    <IonButton size="small" fill="outline" color="medium" routerLink="/reemplazos-v2">
      <IonIcon icon={personAddOutline} slot="start" />
      Cargar reemplazo
    </IonButton>
  );

  const renderFaltaDia = (clave: string, casosDia: CasoFiscalizador[]) => {
    const primero = casosDia[0];
    const telefono = String(primero.telefono ?? "").trim();

    return (
      <IonCard key={clave} style={{ borderLeft: `5px solid ${BORDE_SEMAFORO.ROJO}` }}>
        <IonCardContent>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <IonText color="dark">
              <h2 style={{ fontWeight: 700, margin: 0 }}>{primero.nombre_operario || "-"}</h2>
            </IonText>
            <IonChip color="danger" style={{ flexShrink: 0 }}>
              Falto todo el dia
            </IonChip>
          </div>

          <p style={{ margin: "6px 0 0", fontSize: "0.85rem" }}>
            CI {primero.cedula || "-"} · {moment(primero.fecha_turno).format("DD/MM/YYYY")} ·{" "}
            {casosDia.length === 1 ? "1 asignacion" : `${casosDia.length} asignaciones`}
          </p>

          {casosDia.map((caso) => (
            <div
              key={caso.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 6,
                marginTop: 6,
                fontSize: "0.85rem",
              }}
            >
              <span>
                <IonIcon icon={locationOutline} style={{ verticalAlign: "middle" }} />{" "}
                {caso.punto_servicio || "-"} ·{" "}
                {(caso.hora_entrada || "--:--") + " - " + (caso.hora_salida || "--:--")}
                <IonText color="medium"> · {ETIQUETA_ESTADO[caso.estado] || caso.estado}</IonText>
              </span>
              <IonButton
                size="small"
                fill="clear"
                color="success"
                onClick={() => setAccionActiva({ caso, accion: "confirmar_presencia" })}
              >
                Problema de marcacion
              </IonButton>
            </div>
          ))}

          <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {telefono && (
              <IonButton size="small" fill="outline" href={`tel:${telefono}`}>
                <IonIcon icon={callOutline} slot="start" />
                Llamar
              </IonButton>
            )}
            <IonButton
              size="small"
              color={COLOR_ACCION.falto_dia}
              onClick={() => setAccionActiva({ caso: primero, accion: "falto_dia" })}
            >
              {LABEL_ACCION.falto_dia}
            </IonButton>
            {botonReemplazo}
          </div>
        </IonCardContent>
      </IonCard>
    );
  };

  return (
    <IonPage>
      <Header titulo="Mis Casos" />

      <IonContent id="mis-casos">
        <IonRefresher
          slot="fixed"
          onIonRefresh={async (e) => {
            await cargarCasos(true);
            e.detail.complete();
          }}
        >
          <IonRefresherContent />
        </IonRefresher>

        <div className="container mt-1">
          <IonSegment
            value={soloPendientes ? "pendientes" : "todos"}
            onIonChange={(e) =>
              setSoloPendientes((e.detail.value || "pendientes") === "pendientes")
            }
          >
            <IonSegmentButton value="pendientes">
              <IonLabel>Pendientes</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="todos">
              <IonLabel>Todos</IonLabel>
            </IonSegmentButton>
          </IonSegment>

          <div style={{ display: "flex", gap: 8 }}>
            <IonItem style={{ flex: 1 }}>
              <IonLabel position="stacked">Desde</IonLabel>
              <IonDatetime
                displayFormat="DD/MM/YYYY"
                pickerFormat="DD/MM/YYYY"
                cancelText="Cancelar"
                doneText="Listo"
                value={desde}
                onIonChange={(e) => setDesde(normalizarFecha(e.detail.value))}
              />
            </IonItem>
            <IonItem style={{ flex: 1 }}>
              <IonLabel position="stacked">Hasta</IonLabel>
              <IonDatetime
                displayFormat="DD/MM/YYYY"
                pickerFormat="DD/MM/YYYY"
                cancelText="Cancelar"
                doneText="Listo"
                value={hasta}
                onIonChange={(e) => setHasta(normalizarFecha(e.detail.value))}
              />
            </IonItem>
          </div>

          {cargando && (
            <div style={{ textAlign: "center", padding: 24 }}>
              <IonSpinner /> <p>Cargando casos...</p>
            </div>
          )}

          {!cargando && casos.length === 0 && (
            <IonText color="medium">
              <p style={{ textAlign: "center", padding: 24 }}>
                Sin casos {soloPendientes ? "pendientes" : ""} para el rango seleccionado.
              </p>
            </IonText>
          )}

          {!cargando &&
            agruparCasos(casos).map((item) =>
              item.tipo === "dia" ? renderFaltaDia(item.clave, item.casos) : renderCaso(item.caso)
            )}
        </div>

        <IonAlert
          isOpen={accionActiva !== null}
          header={
            accionActiva ? labelAccion(accionActiva.accion, accionActiva.caso.estado) : ""
          }
          message={
            accionActiva
              ? `${mensajeAccion(accionActiva.accion, accionActiva.caso.estado)} Operario: ${accionActiva.caso.nombre_operario}.`
              : ""
          }
          inputs={[
            {
              name: "observacion",
              type: "textarea",
              placeholder:
                accionActiva && OBSERVACION_OBLIGATORIA.includes(accionActiva.accion)
                  ? "Observacion (obligatoria)"
                  : "Observacion (opcional)",
            },
          ]}
          buttons={[
            { text: "Cancelar", role: "cancel" },
            {
              text: "Confirmar",
              handler: (data) => {
                if (accionActiva) {
                  ejecutarAccion(
                    accionActiva.caso,
                    accionActiva.accion,
                    String(data?.observacion || "")
                  );
                }
              },
            },
          ]}
          onDidDismiss={() => setAccionActiva(null)}
        />
      </IonContent>
    </IonPage>
  );
};

export default MisCasosFiscal;
